import { APP_NAME } from "@ayet-agent-platform/shared";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, ChevronRight, ChevronUp, FolderKanban, LogOut, UserRound } from "lucide-react";
import { Fragment, type JSX, type ReactNode, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";

import { apiFetch } from "@/api";
import { StatusDot } from "@/components/StatusBadge";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarRail,
} from "@/components/ui/sidebar";
import { useSidebar } from "@/components/ui/sidebar-context";
import { useRouteResources } from "@/hooks/use-route-resources";
import type { NavGroupId } from "@/lib/navigation";
import {
  ACCOUNT_MENU,
  ALL_PROJECTS_LABEL,
  MAX_PINNED_AGENTS_PER_PROJECT,
  MAX_PINNED_PROJECTS,
  NAV_GROUPS,
  PINNED_GROUP_LABEL,
  destinationsInGroup,
  isDestinationActive,
  routeProjectId,
} from "@/lib/navigation";
import type { AgentSummary, CurrentUser, ProjectSummary } from "@/models";

function encoded(value: string): string {
  return encodeURIComponent(value);
}

/**
 * Grant filter for a single agent: an admin sees everything, everyone else needs
 * a grant on that agent or on the project holding it. The backend filters too —
 * this keeps the tree honest when a grant is revoked mid-session and a cached
 * list is still on screen.
 */
export function visibleAgent(me: CurrentUser, projectId: string, agentId: string): boolean {
  if (me.role === "admin") return true;
  return me.grants.some(function covers(grant): boolean {
    if (grant.scopeType === "agent") return grant.scopeId === agentId;
    return grant.scopeId === projectId;
  });
}

/**
 * Grant filter for a project. `agentIds` must be the agents of *this* project:
 * an agent-scoped grant only reveals the project that actually holds that agent,
 * so a grant on an agent in project A never exposes project B. Callers that have
 * not loaded the agent list yet pass an empty array, which keeps an unrelated
 * project hidden until its agents prove otherwise.
 */
export function visibleProject(
  me: CurrentUser,
  projectId: string,
  agentIds: readonly string[],
): boolean {
  if (me.role === "admin") return true;
  return me.grants.some(function covers(grant): boolean {
    if (grant.scopeType === "agent") return agentIds.includes(grant.scopeId);
    return grant.scopeId === projectId;
  });
}

/**
 * Health in one dot: disabled beats budget-paused beats healthy, because a
 * disabled agent cannot run at all.
 */
export function agentStatus(agent: AgentSummary): string {
  if (!agent.enabled) return "disabled";
  if (agent.budgetPaused) return "paused";
  return "enabled";
}

/**
 * The projects the pinned group may show, newest intent first: the project the
 * route names, then the accessible catalog in its server order, capped at
 * `MAX_PINNED_PROJECTS`.
 *
 * The cap is what keeps the sidebar off an N+1: only these projects get an
 * agent-list query, however many projects the viewer can read. Projects is the
 * canonical directory for the rest (concept v1.1 §2).
 */
export function pinnedProjects(
  projects: readonly ProjectSummary[],
  currentProjectId: string | undefined,
): readonly ProjectSummary[] {
  const current = projects.filter(function isCurrent(project): boolean {
    return project.id === currentProjectId;
  });
  const others = projects.filter(function isOther(project): boolean {
    return project.id !== currentProjectId;
  });
  return [...current, ...others].slice(0, MAX_PINNED_PROJECTS);
}

/**
 * The agents a pinned branch may render: the first `MAX_PINNED_AGENTS_PER_PROJECT`
 * in server order, plus the agent the route names when it falls outside them.
 *
 * A project with hundreds of agents must not turn the sidebar into its own
 * directory, and it must not hide the page the viewer is on either. `hidden`
 * is what the branch offers as one compact link to the project page instead.
 */
export function pinnedAgents(
  agents: readonly AgentSummary[],
  currentAgentId: string | undefined,
): { readonly shown: readonly AgentSummary[]; readonly hidden: number } {
  if (agents.length <= MAX_PINNED_AGENTS_PER_PROJECT) {
    return { shown: agents, hidden: 0 };
  }
  const head = agents.slice(0, MAX_PINNED_AGENTS_PER_PROJECT);
  const current = agents.find(function isCurrent(agent): boolean {
    return agent.id === currentAgentId;
  });
  // The current agent stays visible even past the cap; server order otherwise.
  const shown = current === undefined || head.includes(current) ? head : [...head, current];
  return { shown, hidden: agents.length - shown.length };
}

interface ProjectTreeEntry {
  readonly project: ProjectSummary;
  /** The bounded set the branch renders, not every visible agent. */
  readonly agents: readonly AgentSummary[];
  /** Visible agents this branch does not render; `0` when there are none. */
  readonly hiddenAgentCount: number;
  readonly isLoading: boolean;
  readonly isError: boolean;
  /** True for the branch holding the current project or agent: open by default. */
  readonly isCurrent: boolean;
}

/**
 * Closes the mobile sheet after a navigation. On a phone the sidebar is an
 * overlay dialog, so leaving it open would cover the page the user just opened.
 */
function useCloseMobileSidebar(): () => void {
  const { isMobile, setOpenMobile } = useSidebar();
  return function closeMobileSidebar(): void {
    if (isMobile) setOpenMobile(false);
  };
}

function AgentNode({
  agent,
  pathname,
}: {
  readonly agent: AgentSummary;
  readonly pathname: string;
}): JSX.Element {
  const closeMobileSidebar = useCloseMobileSidebar();
  const status = agentStatus(agent);
  const base = `/agents/${encoded(agent.id)}`;
  // The agent's own base path or a tab under it — never a bare string prefix:
  // `ops` must not light up while the route names `ops-reporter`.
  const active = pathname === base || pathname.startsWith(`${base}/`);
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        aria-current={active ? "page" : undefined}
        isActive={active}
        onClick={closeMobileSidebar}
        render={<Link to={`${base}/chat`} />}
        size="sm"
        tooltip={agent.name}
      >
        <StatusDot status={status} />
        <span className="truncate">{agent.name}</span>
        {/*
         * The dot alone is decorative, so an unhealthy agent also states its
         * status in text. Collapsed to the icon rail the text has nowhere to
         * go, so it becomes screen-reader only rather than disappearing.
         *
         * A budget pause is named in full here and in the agent header:
         * "paused" alone does not say what paused, and this list is where an
         * operator scanning projects first sees it.
         */}
        {status !== "enabled" && (
          <Fragment>
            {/* The literal space separates the two words in the computed
                accessible name; without it a screen reader announces the link
                as one run-together token. */}{" "}
            {status === "paused" ? (
              <Badge
                className="ml-auto shrink-0 px-1 py-0 text-xs group-data-[collapsible=icon]:sr-only"
                variant="paused"
              >
                Budget paused
              </Badge>
            ) : (
              <span className="ml-auto shrink-0 text-xs text-sidebar-foreground/70 group-data-[collapsible=icon]:sr-only">
                {status}
              </span>
            )}
          </Fragment>
        )}
      </SidebarMenuButton>
      {agent.activeRunCount !== undefined && agent.activeRunCount > 0 && (
        <SidebarMenuBadge>{agent.activeRunCount}</SidebarMenuBadge>
      )}
    </SidebarMenuItem>
  );
}

/**
 * The agent the route names when no pinned branch lists it — its project may
 * sit outside the bounded pinned set. There is no status dot: the agent record
 * may not have arrived, and a guessed dot would claim a health it cannot know.
 */
function CurrentAgentNode({
  agentId,
  name,
}: {
  readonly agentId: string;
  readonly name: string | undefined;
}): JSX.Element {
  const closeMobileSidebar = useCloseMobileSidebar();
  const label = name ?? agentId;
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        aria-current="page"
        isActive
        onClick={closeMobileSidebar}
        render={<Link to={`/agents/${encoded(agentId)}/chat`} />}
        size="sm"
        tooltip={label}
      >
        <span className="truncate">{label}</span>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

function ProjectNode({
  entry,
  pathname,
}: {
  readonly entry: ProjectTreeEntry;
  readonly pathname: string;
}): JSX.Element {
  const closeMobileSidebar = useCloseMobileSidebar();
  const projectPath = `/projects/${encoded(entry.project.id)}`;
  // The project's own pages, not its agents': an agent page marks the agent
  // entry instead, so only ever one item in this branch is the current page.
  const onProject = pathname === projectPath || pathname.startsWith(`${projectPath}/`);
  const [open, setOpen] = useState(entry.isCurrent);
  /**
   * The branch holding the current resource opens as soon as we know it does.
   * An uncontrolled `defaultOpen` would miss it: the agent lists arrive after
   * the first render, so the branch that turns out to hold the current agent
   * would stay shut with that agent hidden inside it.
   */
  useEffect(
    function openCurrentBranch(): void {
      if (entry.isCurrent) setOpen(true);
    },
    [entry.isCurrent],
  );
  function changeOpen(next: boolean): void {
    setOpen(next);
  }
  return (
    // The Collapsible sits inside the list item: `SidebarMenu` renders a `ul`,
    // which may only contain `li` children. `group/project` stays on the
    // Collapsible so the chevron keeps rotating off its `data-open` state.
    <SidebarMenuItem>
      {/*
        Branches are collapsed unless the route is inside this project (§2):
        the sidebar is not the project directory, so it does not expand every
        tree on every page.
      */}
      <Collapsible className="group/project" onOpenChange={changeOpen} open={open}>
        {/*
          The project's name navigates to the project page. Disclosure is the
          chevron's job alone — a label that only toggles a branch is exactly
          the surprise §2 calls out.
        */}
        <SidebarMenuButton
          aria-current={onProject ? "page" : undefined}
          isActive={onProject}
          onClick={closeMobileSidebar}
          render={<Link to={projectPath} />}
          tooltip={entry.project.name}
        >
          <FolderKanban />
          <span className="truncate">{entry.project.name}</span>
        </SidebarMenuButton>
        <CollapsibleTrigger
          render={
            <SidebarMenuAction aria-label={`Agents in ${entry.project.name}`}>
              <ChevronRight className="transition-transform group-data-open/project:rotate-90" />
            </SidebarMenuAction>
          }
        />
        <CollapsibleContent>
          {/*
            `w-auto`, not the menu's own `w-full`: a full-width list plus its
            indent is wider than the sidebar, which is what scrolled it sideways
            (#163 R7).
          */}
          <SidebarMenu className="ml-3 w-auto min-w-0 border-l border-sidebar-border pl-2">
            {entry.isLoading && (
              <SidebarMenuItem>
                <SidebarMenuSkeleton showIcon />
              </SidebarMenuItem>
            )}
            {entry.isError && (
              <li className="px-2 py-1 text-xs text-destructive">Agents unavailable</li>
            )}
            {entry.agents.map(function agentEntry(agent): ReactNode {
              return <AgentNode agent={agent} key={agent.id} pathname={pathname} />;
            })}
            {entry.hiddenAgentCount > 0 && (
              <SidebarMenuItem>
                {/*
                  One compact link, never the rest of the list: the project page
                  is the directory for everything this branch bounds away.
                */}
                <SidebarMenuButton
                  onClick={closeMobileSidebar}
                  render={<Link to={projectPath} />}
                  size="sm"
                  tooltip={`${String(entry.hiddenAgentCount)} more agents in ${entry.project.name}`}
                >
                  <span className="truncate text-xs text-sidebar-foreground/70">
                    +{String(entry.hiddenAgentCount)} more{" "}
                    {entry.hiddenAgentCount === 1 ? "agent" : "agents"}
                  </span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}
            {!entry.isLoading && !entry.isError && entry.agents.length === 0 && (
              <li className="px-2 py-1 text-xs text-sidebar-foreground/60">No agents</li>
            )}
          </SidebarMenu>
        </CollapsibleContent>
      </Collapsible>
    </SidebarMenuItem>
  );
}

/** Rendered above the pinned resources; Administration follows them. */
const WORKSPACE_GROUPS: readonly NavGroupId[] = ["workspace", "monitoring"];
const ADMINISTRATION_GROUPS: readonly NavGroupId[] = ["administration"];

/**
 * The six primary destinations, grouped (§2).
 *
 * `groups` selects which of `NAV_GROUPS` this instance renders, always in
 * `NAV_GROUPS` order. The sidebar draws Workspace and Monitoring, then the
 * pinned resources, then Administration — so Settings stays last and Pinned
 * sits where the approved layout puts it, between Monitoring and
 * Administration.
 */
function DestinationGroups({
  admin,
  groups,
  pathname,
}: {
  readonly admin: boolean;
  readonly groups: readonly NavGroupId[];
  readonly pathname: string;
}): JSX.Element {
  const closeMobileSidebar = useCloseMobileSidebar();
  return (
    <Fragment>
      {NAV_GROUPS.filter(function selected(group): boolean {
        return groups.includes(group.id);
      }).map(function navGroup(group): ReactNode {
        const destinations = destinationsInGroup(group.id, admin);
        if (destinations.length === 0) return null;
        return (
          <SidebarGroup key={group.id}>
            <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
            <SidebarGroupContent>
              <nav aria-label={group.label}>
                <SidebarMenu>
                  {destinations.map(function destinationItem(destination): ReactNode {
                    const Icon = destination.icon;
                    const active = isDestinationActive(destination, pathname);
                    return (
                      <SidebarMenuItem key={destination.id}>
                        {/*
                          Selected state is a background, a heavier weight and
                          aria-current together — never colour alone (§8).
                        */}
                        <SidebarMenuButton
                          aria-current={active ? "page" : undefined}
                          isActive={active}
                          onClick={closeMobileSidebar}
                          render={<Link to={destination.to} />}
                          tooltip={destination.label}
                        >
                          <Icon />
                          <span>{destination.label}</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </nav>
            </SidebarGroupContent>
          </SidebarGroup>
        );
      })}
    </Fragment>
  );
}

/** The signed-in identity and its own destinations, in the footer (§2). */
function AccountMenu({ me }: { readonly me: CurrentUser }): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const closeMobileSidebar = useCloseMobileSidebar();
  const logout = useMutation({
    mutationFn: function logoutRequest(): Promise<unknown> {
      return apiFetch("/auth/logout", { method: "POST" });
    },
    onSettled: function loggedOut(): void {
      queryClient.clear();
      navigate("/login", { replace: true });
    },
  });
  function logOut(): void {
    logout.mutate();
  }
  return (
    <nav aria-label="Account">
      <SidebarMenu>
        <SidebarMenuItem>
          <Collapsible className="group/account">
            <CollapsibleTrigger
              render={
                <SidebarMenuButton className="h-auto py-1.5" tooltip={me.name}>
                  <UserRound />
                  <span className="grid min-w-0 flex-1 text-left group-data-[collapsible=icon]:hidden">
                    <span className="truncate text-xs font-medium">{me.name}</span>
                    <span className="truncate text-xs text-sidebar-foreground/60">{me.email}</span>
                  </span>
                  <ChevronUp className="transition-transform group-data-open/account:rotate-180 group-data-[collapsible=icon]:hidden" />
                </SidebarMenuButton>
              }
            />
            <CollapsibleContent>
              {/* `w-auto` for the same reason as the agent branch: indent, not overflow. */}
              <SidebarMenu className="ml-1 w-auto min-w-0 border-l border-sidebar-border pl-2 group-data-[collapsible=icon]:ml-0 group-data-[collapsible=icon]:border-0 group-data-[collapsible=icon]:pl-0">
                {ACCOUNT_MENU.map(function accountEntry(entry): ReactNode {
                  const Icon = entry.icon;
                  return (
                    <SidebarMenuItem key={entry.to}>
                      <SidebarMenuButton
                        onClick={closeMobileSidebar}
                        render={<Link to={entry.to} />}
                        size="sm"
                        tooltip={entry.label}
                      >
                        <Icon />
                        <span className="truncate">{entry.label}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
                <SidebarMenuItem>
                  <SidebarMenuButton disabled={logout.isPending} onClick={logOut} size="sm">
                    <LogOut />
                    <span>Sign out</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </CollapsibleContent>
          </Collapsible>
        </SidebarMenuItem>
      </SidebarMenu>
    </nav>
  );
}

export interface AppSidebarProps {
  readonly me: CurrentUser;
}

export function AppSidebar({ me }: AppSidebarProps): JSX.Element {
  const location = useLocation();
  const pathname = location.pathname;
  const closeMobileSidebar = useCloseMobileSidebar();
  const resources = useRouteResources(pathname);
  const currentProjectId = routeProjectId(pathname);
  const projects = useQuery({
    queryKey: ["projects"],
    queryFn: function fetchProjects(): Promise<readonly ProjectSummary[]> {
      return apiFetch("/projects");
    },
  });
  const pinned = pinnedProjects(projects.data ?? [], currentProjectId);
  // One agents query per *pinned* project, so the grant filter can tell which
  // project a single agent-scoped grant belongs to without fetching the agents
  // of every project in the catalog.
  const agentQueries = useQueries({
    queries: pinned.map(function agentsQuery(project) {
      return {
        queryKey: ["projects", project.id, "agents"],
        queryFn: function fetchAgents(): Promise<readonly AgentSummary[]> {
          return apiFetch(`/projects/${encoded(project.id)}/agents`);
        },
      };
    }),
  });

  const tree: ProjectTreeEntry[] = [];
  let currentAgentListed = false;
  for (const [index, project] of pinned.entries()) {
    const query = agentQueries[index];
    const all = query?.data ?? [];
    const ids = all.map(function agentId(agent): string {
      return agent.id;
    });
    if (!visibleProject(me, project.id, ids)) continue;
    const agents = all.filter(function grantedAgent(agent): boolean {
      return visibleAgent(me, project.id, agent.id);
    });
    const holdsCurrentAgent =
      resources.agentId !== undefined &&
      agents.some(function isCurrent(agent): boolean {
        return agent.id === resources.agentId;
      });
    if (holdsCurrentAgent) currentAgentListed = true;
    const bounded = pinnedAgents(agents, resources.agentId);
    tree.push({
      project,
      agents: bounded.shown,
      hiddenAgentCount: bounded.hidden,
      isLoading: query?.isLoading ?? true,
      isError: query?.isError ?? false,
      isCurrent: holdsCurrentAgent || project.id === currentProjectId,
    });
  }
  const agentsLoading = agentQueries.some(function loading(query): boolean {
    return query.isLoading;
  });
  const agentsFailed = agentQueries.some(function failed(query): boolean {
    return query.isError;
  });
  /**
   * A failed agent list can neither prove nor disprove an agent-scoped grant, so
   * an "ask for access" line would be a guess stated as fact. Only claim the
   * tree is empty once every agent list actually arrived.
   */
  const nothingAccessible =
    projects.isSuccess && !agentsLoading && !agentsFailed && tree.length === 0;
  const pinnedPending = projects.isLoading || (agentsLoading && tree.length === 0);
  /**
   * The agent the route names, when the pinned branches do not already list it
   * — its project may sit outside the bounded set. The name comes from the
   * agent query the page itself runs, so this costs no extra request, and an
   * unresolved name falls back to the id the URL already shows.
   *
   * Only once the pinned agent lists have arrived: before that "no branch
   * holds it" is unknown, not false, and claiming it would flash an entry that
   * the loaded branch then takes over.
   */
  const strayAgent =
    resources.agentId !== undefined && !currentAgentListed && !projects.isLoading && !agentsLoading
      ? resources.agentId
      : undefined;
  /**
   * `agentsFailed` belongs here: with the projects list in hand but every agent
   * list failing, the tree is empty for a reason the user needs to see, and
   * without the group the "Agent list unavailable" line inside it never
   * rendered on a non-agent route.
   */
  const showPinned =
    tree.length > 0 ||
    strayAgent !== undefined ||
    projects.isError ||
    agentsFailed ||
    pinnedPending;
  /**
   * Access guidance is for someone who could be granted more. An admin already
   * sees everything, so for them an empty catalog means there are no projects
   * yet — telling them to ask an administrator would be false.
   */
  const showAccessGuidance = nothingAccessible && strayAgent === undefined && me.role !== "admin";

  return (
    <Sidebar collapsible="icon" data-testid="app-sidebar" label={APP_NAME}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={closeMobileSidebar}
              render={<Link to="/" />}
              size="lg"
              tooltip={APP_NAME}
            >
              <span className="grid size-7 shrink-0 place-items-center bg-brand text-xs font-semibold text-brand-foreground">
                AP
              </span>
              <span className="truncate font-semibold">{APP_NAME}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <DestinationGroups
          admin={me.role === "admin"}
          groups={WORKSPACE_GROUPS}
          pathname={pathname}
        />

        {showPinned && (
          <SidebarGroup>
            <SidebarGroupLabel>{PINNED_GROUP_LABEL}</SidebarGroupLabel>
            <SidebarGroupContent>
              <nav aria-label={PINNED_GROUP_LABEL}>
                <SidebarMenu>
                  {pinnedPending && (
                    <SidebarMenuItem>
                      <SidebarMenuSkeleton showIcon />
                    </SidebarMenuItem>
                  )}
                  {projects.isError && (
                    <li className="px-2 py-1 text-xs text-destructive">Projects unavailable</li>
                  )}
                  {projects.isSuccess && agentsFailed && (
                    <li className="px-2 py-1 text-xs text-destructive">Agent list unavailable</li>
                  )}
                  {tree.map(function projectNode(entry): ReactNode {
                    return <ProjectNode entry={entry} key={entry.project.id} pathname={pathname} />;
                  })}
                  {strayAgent !== undefined && (
                    <CurrentAgentNode agentId={strayAgent} name={resources.agent?.name} />
                  )}
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      onClick={closeMobileSidebar}
                      render={<Link to="/projects" />}
                      size="sm"
                      tooltip={ALL_PROJECTS_LABEL}
                    >
                      <ArrowRight />
                      <span>{ALL_PROJECTS_LABEL}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </nav>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/*
          Zero-grant guidance instead of an empty pinned group (§8): the user is
          told how to get access rather than shown a menu with nothing in it.
        */}
        {showAccessGuidance && (
          <SidebarGroup>
            <SidebarGroupContent>
              <p
                className="px-2 py-1 text-xs text-sidebar-foreground/70 group-data-[collapsible=icon]:hidden"
                data-testid="sidebar-access-guidance"
              >
                No accessible projects. Ask an administrator for project or agent access.
              </p>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* Administration comes last, so Settings sits at the foot of the list. */}
        <DestinationGroups
          admin={me.role === "admin"}
          groups={ADMINISTRATION_GROUPS}
          pathname={pathname}
        />
      </SidebarContent>

      <SidebarFooter>
        <AccountMenu me={me} />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
